import { useTranslation } from 'react-i18next';

import type { EtiquetaAyuda, PasoAyuda, PreguntaAyuda } from './pasos';

function uno(selector: string): Element[] {
  const el = document.querySelector(selector);
  return el ? [el] : [];
}

function todos(selector: string): Element[] {
  return [...document.querySelectorAll(selector)];
}

/** El primero de `selectores` que esté en pantalla. */
function primero(...selectores: string[]): Element[] {
  for (const selector of selectores) {
    const el = document.querySelector(selector);
    if (el) {
      return [el];
    }
  }
  return [];
}

/** Rótulos de latón sobre los elementos que estén en pantalla. */
function etiquetasDe(
  partes: ReadonlyArray<readonly [string, string, boolean]>,
): EtiquetaAyuda[] {
  const etiquetas: EtiquetaAyuda[] = [];
  for (const [selector, texto, debajo] of partes) {
    const el = document.querySelector(selector);
    if (el) {
      etiquetas.push({ el, texto, debajo });
    }
  }
  return etiquetas;
}

const DESGLOSE = '[data-ayuda="ficha-desglose"]';
const ESTADO = '[data-testid="ficha-estado"], [data-testid="ficha-falta"]';

/** Lo que la ayuda necesita saber de la ficha para elegir sus textos. */
export type DatosAyudaFicha = {
  nombre: string;
  esIngreso: boolean;
  /** Etiqueta de arriba (`estadoFila`). */
  estado:
    | 'gastado-de-mas'
    | 'falta'
    | 'sobrefinanciada'
    | 'financiada'
    | 'sin-objetivo'
    | 'ignorada';
  /** Lo que falta este mes, ya con formato, si salen «Asignar» y «Mover…». */
  falta: string | null;
  /** El objetivo dice «Atrasado» (nunca en una travesía). */
  atrasado: boolean;
  hayObjetivo: boolean;
  /** Meta con fecha que se ve como «Travesía». */
  travesia: boolean;
  /** «Saldo» dibuja el camino hasta una meta con fecha. */
  metaEnSaldo: boolean;
  pagoFijo: boolean;
  oculta: boolean;
};

/**
 * La ayuda de la ficha de una categoría (maqueta v2 aprobada por Vadik el
 * 27-oct-2026, con «cómo pongo o cambio un objetivo»): el recorrido entero y
 * siete preguntas. Gavi cambia la vista de «Evolución», despliega «Ver
 * movimientos» y abre el editor del objetivo (lo cierra con «Cancelar»),
 * pero nunca pulsa nada que guarde: ni «Guardar», ni «Asignar», ni «Borrar».
 */
export function usePreguntasFicha(datos: DatosAyudaFicha): PreguntaAyuda[] {
  const { t } = useTranslation();
  const {
    nombre,
    esIngreso,
    estado,
    falta,
    atrasado,
    hayObjetivo,
    travesia,
    metaEnSaldo,
    pagoFijo,
    oculta,
  } = datos;
  const enRojo = estado === 'gastado-de-mas';
  const noIngreso = () => !esIngreso;

  const textoEstado = (() => {
    switch (estado) {
      case 'financiada':
      case 'sobrefinanciada':
        return t(
          'At the top, what it has left this month. "Covered this month" is not about spending: it says you have already assigned what its target asked for. The ring is the same as a percentage.',
        );
      case 'falta':
        return falta
          ? t(
              'At the top, what it has left this month. It is {{amount}} short of what its target asked for: "Assign" puts it in from Ready to Assign; "Move…" brings it from another category.',
              { amount: falta },
            )
          : t(
              'At the top, what it had left that month; the label says how much it was short of what its target asked for.',
            );
      case 'gastado-de-mas':
        return t(
          'At the top, what it has left this month: in red because you have spent more than it had. Nothing serious; further on I show you how to cover it.',
        );
      case 'ignorada':
        return t(
          'At the top, what it has left this month. "Enough this month" is your mark: this month you ask nothing more of it, even if the target falls short. The same ✓ removes it.',
        );
      default:
        return t(
          'At the top, what this category has left this month. Without a target, the label only says whether it has money ("Money available") or not ("Nothing pending").',
        );
    }
  })();

  const evolucion = () => uno('[data-testid="ficha-evolucion"]');

  const todo: PasoAyuda[] = [
    {
      id: 'hola',
      escena: 'normal',
      radio: 14,
      texto: t(
        'Hi. This is the card for "{{category}}": how much it has, how it is going and what you can change. I will point with my wing; tapping is up to you, wings are no good for that.',
        { category: nombre },
      ),
      objetivo: () => uno('[data-ayuda="titulo-ficha"]'),
    },
    {
      id: 'disponible',
      escena: 'normal',
      radio: 18,
      texto: textoEstado,
      objetivo: () => [
        ...uno('[data-testid="ficha-disponible"]'),
        ...todos(ESTADO),
        ...uno('[data-testid="ficha-anillo"]'),
      ],
      etiquetas: () =>
        etiquetasDe([
          ['[data-testid="ficha-asignar-falta"]', t('Assign'), true],
          ['[data-testid="ficha-mover-falta"]', t('Move…'), true],
        ]),
    },
    {
      id: 'desglose',
      escena: 'normal',
      radio: 12,
      texto: t(
        'The three figures below: what was left over from last month, plus what you assigned this month, minus the activity (what you spent; a refund adds). Tap "Activity" to see its transactions.',
      ),
      objetivo: () => uno(DESGLOSE),
    },
    {
      id: 'objetivo',
      escena: 'normal',
      radio: 18,
      texto: travesia
        ? t(
            'A target with a date is a voyage: the bottle moves forward with what you set aside and tells you whether you will make it in time. Below, the target; tap it to change it.',
          )
        : hayObjetivo
          ? t(
              'The target is what you ask of this category. "Steady course": all good. "Behind": you have not yet assigned what this month needed. Tap it to change it.',
            )
          : t(
              'Without a target, the category asks nothing of you. "Add target" sets an amount a month, a "refill up to" or a goal with a date; from then on I warn you if it falls short.',
            ),
      objetivo: () =>
        primero(
          '[data-testid="ficha-objetivo"]',
          '[data-testid="botella-ficha"]',
        ),
      etiquetas: () =>
        etiquetasDe([
          ['[data-testid="ficha-ignorar"]', t('Enough this month'), true],
        ]),
    },
    {
      id: 'editor',
      escena: 'ficha-editor',
      radio: 16,
      texto: t(
        'Tapping it opens it right here. At the top you choose how often and, in "I need", how much. "Save" keeps it; "Cancel" leaves it as it was.',
      ),
      objetivo: () => uno('[data-testid="objetivo-editor"]'),
      etiquetas: () =>
        etiquetasDe([
          ['[data-testid="objetivo-cancelar"]', t('Cancel'), true],
          ['[data-testid="objetivo-guardar"]', t('Save'), true],
        ]),
    },
    {
      id: 'evolucion',
      escena: 'ficha-gasto',
      radio: 16,
      texto: t(
        'Evolution, month by month. In "Spend", the bars are what you spent each month and the line, what you assigned. The "Usual" band is where your normal months tend to sit, without the peaks.',
      ),
      objetivo: evolucion,
    },
    {
      id: 'saldo',
      escena: 'ficha-saldo',
      radio: 16,
      texto: metaEnSaldo
        ? t(
            'In "Balance" you see how much the category had at the end of each month. The dotted line is the path to the goal at today\'s instalment; the dash, the goal.',
          )
        : t(
            'In "Balance" you see how much the category had at the end of each month.',
          ),
      objetivo: evolucion,
    },
    {
      id: 'meses',
      escena: 'ficha-meses',
      radio: 16,
      texto: t(
        'In "Months", two years on one map: the darker, the more spending. If two months take half the year, it says so below. Tap one to see its transactions.',
      ),
      objetivo: evolucion,
    },
    {
      id: 'movimientos',
      escena: 'ficha-movimientos',
      radio: 12,
      texto: t(
        '"See transactions" opens that month\'s entries right here, under the chart. Tap one to see it inside.',
      ),
      objetivo: () => uno('[data-testid="movimientos-mes"]'),
    },
    {
      id: 'gavi',
      escena: 'normal',
      radio: 16,
      disponible: noIngreso,
      texto: t(
        'What Gavi thinks: tips on this category drawn from your own months, with fixed rules, no guessing. I suggest and you decide; each tip says why.',
      ),
      objetivo: () => uno('[data-testid="ficha-asesor"]'),
    },
    {
      id: 'cuenta',
      escena: 'normal',
      radio: 14,
      disponible: noIngreso,
      texto: t(
        'Account: where this category\'s money usually comes from. On "Automatic" I take it from your history. That way "Move money" and "Cover" suggest categories from the same account.',
      ),
      objetivo: () => uno('[data-testid="ficha-cuenta"]'),
    },
    {
      id: 'pago',
      escena: 'normal',
      radio: 14,
      disponible: noIngreso,
      texto: pagoFijo
        ? t(
            'It is a fixed payment: under the switch you see how much you set aside, when it is charged and from which account. When the charge arrives I mark it "Paid"; if I do not see it, mark it here yourself.',
          )
        : t(
            '"Fixed payment" is for bills that repeat: rent, electricity, a subscription. Rumbo pays nothing: it plans for it, spots it when it arrives and marks it "Paid". If it misses it, mark it here.',
          ),
      objetivo: () => uno('[data-testid="ficha-pago"]'),
      etiquetas: () =>
        etiquetasDe([
          ['label[for="pago-fijo"]', t('Fixed payment'), false],
          [
            '[data-testid="ficha-marcar-pagada"]',
            t('Mark as paid this month'),
            true,
          ],
        ]),
    },
    {
      id: 'notas',
      escena: 'normal',
      radio: 14,
      texto: t(
        'In Notes you write down what you want to remember about this category: the charge day, a contract number. "Edit" opens the text.',
      ),
      objetivo: () => uno('[data-testid="ficha-notas"]'),
      etiquetas: () =>
        etiquetasDe([['[data-testid="ficha-notas-editar"]', t('Edit'), false]]),
    },
    {
      id: 'pie',
      escena: 'normal',
      radio: 12,
      texto: oculta
        ? t(
            '"Rename" changes the name. It is hidden: "Show" brings it back to the Plan. "Delete" removes it for good: it always asks first and cannot be undone. I only point at it.',
          )
        : t(
            '"Rename" changes the name. "Hide" takes it out of the Plan without losing anything. "Delete" removes it for good: it always asks first and cannot be undone. I only point at it.',
          ),
      objetivo: () => uno('[data-ayuda="ficha-pie"]'),
    },
    {
      id: 'brujula',
      escena: 'normal',
      radio: 30,
      toque: true,
      texto: t('Now tap the compass, at the bottom right.'),
      objetivo: () => uno('[data-testid="brujula-opciones"]'),
    },
    {
      id: 'opciones',
      escena: 'brujula',
      radio: 20,
      texto: esIngreso
        ? t(
            'Here only "Add transaction": it notes an income already in this category.',
          )
        : enRojo
          ? t(
              '"Cover" brings from another category what it needs to get out of the red. "Pin to home" puts it on your Home. "Add transaction" notes an expense already in this category.',
            )
          : t(
              '"Move money" brings money from another category. "Pin to home" puts it on your Home. "Add transaction" notes an expense already in this category.',
            ),
      objetivo: () => todos('[data-testid^="opcion-"]'),
    },
    {
      id: 'final',
      escena: 'normal',
      radio: 24,
      final: true,
      texto: t(
        'That is all. If you forget something, tap the porthole and I will tell you again. Here the porthole has no terrace surcharge, unlike in my harbour.',
      ),
      objetivo: () => uno('[data-testid="ojo-de-buey"]'),
    },
  ];

  const editor = (
    paso: Omit<PasoAyuda, 'escena'> & { escena?: PasoAyuda['escena'] },
  ): PasoAyuda => ({ escena: 'ficha-editor', radio: 14, ...paso });

  return [
    { id: 'todo', texto: t('Tell me about this screen'), pasos: todo },
    {
      id: 'objetivo',
      texto: t('How do I set or change a target?'),
      pasos: [
        {
          id: 'abrir',
          escena: 'normal',
          radio: 14,
          texto: t(
            'Tap the target card; if it has none yet, "Add target". It opens right here, without changing screen.',
          ),
          objetivo: () =>
            primero(
              '[data-testid="objetivo-resumen"]',
              '[data-testid="objetivo-anadir"]',
            ),
        },
        editor({
          id: 'tipos',
          escena: 'ficha-editor-tipos',
          texto: t(
            'At the top you choose how often: "Every month" or "Week" for expenses that repeat, "Year" for a yearly bill and "Once" for a goal with a date, like a trip.',
          ),
          objetivo: () => todos('[data-testid^="objetivo-tipo-"]'),
        }),
        editor({
          id: 'cuanto',
          texto: t(
            'In "I need" you put how much and, next to it, by when: the day of the month or, for a goal, the date.',
          ),
          objetivo: () => uno('[data-ayuda="objetivo-cuanto"]'),
        }),
        editor({
          id: 'modo',
          texto: t(
            '"Set aside another X" adds the same every month, even if there is some left. "Refill up to X" only puts in what is missing to get back to X.',
          ),
          objetivo: () => uno('[data-ayuda="objetivo-modo"]'),
        }),
        editor({
          id: 'sugerencias',
          texto: t(
            'Not sure how much? "Monthly average" and "Last month" come from your months: tap one and I put it in "I need".',
          ),
          objetivo: () => uno('[data-testid="objetivo-sugerencias"]'),
        }),
        editor({
          id: 'cuota',
          escena: 'ficha-editor-una-vez',
          texto: t(
            'With a date, I tell you how much it works out per month until you get there. If the instalment is too big for you, move the date further away.',
          ),
          objetivo: () => uno('[data-testid="objetivo-cuota"]'),
        }),
        editor({
          id: 'botones',
          texto: t(
            '"Save" keeps it. "Cancel" leaves it as it was. "Remove target" deletes it, but does not touch the category\'s money.',
          ),
          objetivo: () => uno('[data-ayuda="objetivo-botones"]'),
          etiquetas: () =>
            etiquetasDe([
              ['[data-testid="objetivo-guardar"]', t('Save'), false],
              ['[data-testid="objetivo-cancelar"]', t('Cancel'), false],
              ['[data-testid="objetivo-eliminar"]', t('Remove target'), false],
            ]),
        }),
      ],
    },
    {
      id: 'disponible',
      texto: t('What is "Available" and where does it come from?'),
      pasos: [
        {
          id: 'que-es',
          escena: 'normal',
          radio: 14,
          texto: t(
            '"Available" is what this category has left to spend this month. It is not what you spent: it is what it still has.',
          ),
          objetivo: () => uno('[data-testid="ficha-disponible"]'),
        },
        {
          id: 'de-donde',
          escena: 'normal',
          radio: 12,
          texto: t(
            'It comes from adding what was left over last month and what you assigned this month, and subtracting the activity (what you spent). A refund makes the activity add.',
          ),
          objetivo: () => uno(DESGLOSE),
        },
        {
          id: 'etiqueta',
          escena: 'normal',
          radio: 14,
          texto: textoEstado,
          objetivo: () => todos(ESTADO),
        },
        {
          id: 'anillo',
          escena: 'normal',
          radio: 42,
          texto: t(
            'The ring is the same as a percentage: how much of what the month asked for is already assigned. With a goal with a date, how far along the goal you are.',
          ),
          objetivo: () => uno('[data-testid="ficha-anillo"]'),
        },
        {
          id: 'sobra',
          escena: 'normal',
          radio: 12,
          texto: t(
            'What is left over is not lost: it moves to next month as "From last month". Overspending is not carried: it comes out of Ready to Assign next month.',
          ),
          objetivo: () => uno(DESGLOSE),
        },
      ],
    },
    {
      id: 'atrasado',
      texto: t('What does "Behind" mean and what do I do?'),
      pasos: [
        {
          id: 'aviso',
          escena: 'normal',
          radio: 14,
          disponible: () => atrasado,
          texto: t(
            '"Behind" only warns you: this month you have not yet assigned what the target asked for. It is not a debt and nothing is charged.',
          ),
          objetivo: () => uno('[data-testid="objetivo-estado"]'),
        },
        {
          id: 'resumen',
          escena: 'normal',
          radio: 14,
          disponible: () => atrasado,
          texto: t(
            'Below you have how much is missing ("Missing X") and the instalment ("About X/month"). With a goal with a date, the instalment is spread until that date.',
          ),
          objetivo: () => uno('[data-testid="objetivo-resumen"]'),
        },
        {
          id: 'ponerse',
          escena: 'normal',
          radio: 14,
          disponible: () => atrasado && falta != null,
          texto: t(
            'To catch up: "Assign" takes it from Ready to Assign; "Move…", from another category. If this month it does not apply, the ✓ "Enough this month" leaves it alone.',
          ),
          objetivo: () => [
            ...uno('[data-testid="ficha-falta"]'),
            ...uno('[data-testid="ficha-ignorar"]'),
          ],
          etiquetas: () =>
            etiquetasDe([
              ['[data-testid="ficha-asignar-falta"]', t('Assign'), true],
              ['[data-testid="ficha-mover-falta"]', t('Move…'), true],
              ['[data-testid="ficha-ignorar"]', t('Enough this month'), true],
            ]),
        },
        {
          id: 'mes-cerrado',
          escena: 'normal',
          radio: 14,
          disponible: () => atrasado && falta == null,
          texto: t(
            'It is a month already closed: you do not assign here. What you did not put in then is not charged now; look at the current month in the Plan.',
          ),
          objetivo: () => uno('[data-testid="ficha-estado"]'),
        },
        {
          id: 'cambiar',
          escena: 'normal',
          radio: 14,
          disponible: () => atrasado,
          texto: t(
            'If the instalment is too big for you, tap the target and change it. With an amount a month, what you do not put in does not pile up; with a goal with a date, the instalment goes up to get there in time.',
          ),
          objetivo: () => uno('[data-testid="objetivo-resumen"]'),
        },
      ],
    },
    {
      id: 'pasarse',
      texto: t('What happens if I overspend?'),
      pasos: [
        {
          id: 'rojo',
          escena: 'normal',
          radio: 14,
          disponible: noIngreso,
          texto: enRojo
            ? t(
                'It is in the red: you have spent more than it had. It is not a failure: you cover it with money from another category, and it is best to do it this month.',
              )
            : t(
                'If you spend more than it has, "Available" turns red and the label says "Overspent". It is not a failure: you cover it with money from another category.',
              ),
          objetivo: () => [
            ...uno('[data-testid="ficha-disponible"]'),
            ...(enRojo ? uno('[data-testid="ficha-estado"]') : []),
          ],
        },
        {
          id: 'brujula',
          escena: 'normal',
          radio: 30,
          toque: true,
          disponible: noIngreso,
          texto: t('Tap the compass, at the bottom right.'),
          objetivo: () => uno('[data-testid="brujula-opciones"]'),
        },
        {
          id: 'cubrir',
          escena: 'brujula',
          radio: 14,
          disponible: noIngreso,
          texto: t(
            '"Cover" (or "Move money") brings what is missing from a category that has some to spare: you choose which one and how much. No need to touch past months.',
          ),
          objetivo: () => uno('[data-testid="opcion-mover"]'),
        },
        {
          id: 'si-no',
          escena: 'normal',
          radio: 12,
          disponible: noIngreso,
          texto: t(
            'If you leave it in the red, next month this category starts at zero and what was missing comes out of Ready to Assign.',
          ),
          objetivo: () => uno(DESGLOSE),
        },
      ],
    },
    {
      id: 'pago-fijo',
      texto: t('How do I turn it into a fixed payment?'),
      pasos: [
        {
          id: 'interruptor',
          escena: 'normal',
          radio: 14,
          disponible: noIngreso,
          texto: t(
            'Turn on "Fixed payment". It is for bills that repeat: rent, electricity, a subscription. It is saved straight away.',
          ),
          objetivo: () => uno('[data-testid="ficha-pago"]'),
          etiquetas: () =>
            etiquetasDe([
              ['label[for="pago-fijo"]', t('Fixed payment'), false],
            ]),
        },
        {
          id: 'importe',
          escena: 'normal',
          radio: 14,
          disponible: noIngreso,
          texto: t(
            'The amount and how often it is charged come from the target: if it has none, give it one. Under the switch you choose the charge day and the "From:" account.',
          ),
          objetivo: () =>
            primero(
              '[data-testid^="pago-fijo-"]',
              '[data-testid="ficha-objetivo"]',
            ),
        },
        {
          id: 'pagado',
          escena: 'normal',
          radio: 14,
          disponible: noIngreso,
          texto: t(
            'Every month I wait for it and, when I see the charge, I mark it "Paid this month". If I do not see it, tap "Mark as paid this month". Rumbo pays nothing for you.',
          ),
          objetivo: () =>
            primero(
              '[data-testid="ficha-marcar-pagada"]',
              '[data-testid="ficha-pago"]',
            ),
        },
        {
          id: 'apagar',
          escena: 'normal',
          radio: 14,
          disponible: noIngreso,
          texto: t(
            'To stop treating it as a fixed payment, turn the switch off. Nothing is deleted: neither the target nor the transactions.',
          ),
          objetivo: () => uno('[data-testid="ficha-pago"]'),
        },
      ],
    },
    {
      id: 'gavi',
      texto: t('What is "What Gavi thinks"?'),
      pasos: [
        {
          id: 'que-es',
          escena: 'normal',
          radio: 16,
          disponible: noIngreso,
          texto: t(
            'Tips on this category made from your own months: whether it falls short, whether it has too much, whether you rarely use it. Fixed rules, no guessing.',
          ),
          objetivo: () => uno('[data-testid="ficha-asesor"]'),
        },
        {
          id: 'consejo',
          escena: 'normal',
          radio: 14,
          disponible: noIngreso,
          texto: t(
            'Each tip says why and, if it has a button, what it would do. I suggest and you decide: nothing changes until you tap it yourself. "See more" opens the rest.',
          ),
          objetivo: () =>
            primero(
              '[data-testid^="consejo-"]',
              '[data-testid="ficha-asesor"]',
            ),
        },
        {
          id: 'meses',
          escena: 'normal',
          radio: 16,
          disponible: noIngreso,
          texto: t(
            'At the bottom it says how many months I base it on. If there is nothing to say, I say so: "Nothing to say about this category for now".',
          ),
          objetivo: () => uno('[data-testid="ficha-asesor"]'),
        },
      ],
    },
    {
      id: 'ocultar-borrar',
      texto: t('What is the difference between Hide and Delete?'),
      pasos: [
        {
          id: 'ocultar',
          escena: 'normal',
          radio: 12,
          texto: t(
            '"Hide" takes it out of your Plan and the lists, but you lose nothing: its transactions, its money and its history stay there.',
          ),
          objetivo: () => uno('[data-testid="ficha-ocultar"]'),
        },
        {
          id: 'recuperar',
          escena: 'normal',
          radio: 12,
          texto: t(
            'To get it back: in the Plan, compass › "Edit categories", "Hidden" block at the end. Or from this card: the button becomes "Show".',
          ),
          objetivo: () => uno('[data-testid="ficha-ocultar"]'),
        },
        {
          id: 'borrar',
          escena: 'normal',
          radio: 12,
          texto: t(
            '"Delete" removes it completely. It always asks you first; if it has transactions or money, you also choose which category they move to.',
          ),
          objetivo: () => uno('[data-testid="ficha-eliminar"]'),
        },
        {
          id: 'sin-deshacer',
          escena: 'normal',
          radio: 12,
          texto: t(
            'There is no undo. If in doubt, hide it: that can be reversed. Here I only point; tapping, never.',
          ),
          objetivo: () => uno('[data-testid="ficha-eliminar"]'),
        },
      ],
    },
  ];
}
